import { mergeClassNames } from '@/utils/mergeClassNames';
import type { RadioProps as AriaRadioProps } from 'react-aria-components';
import { Radio as AriaRadio } from 'react-aria-components';
import styles from './Radio.module.scss';

// TODO: extend AriaRadioProps
export type RadioProps = {
  /** The value of the radio button, used when submitting an HTML form. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio#Value | MDN}. */
  value: string;
  /** The text label to render next to the radio indicator. */
  children?: AriaRadioProps['children'];
  /** Whether the radio button is disabled or not. Shows that a selection exists, but is not available in that circumstance. */
  isDisabled?: boolean;
  /** Whether the element should receive focus on render. */
  autoFocus?: boolean;

  // ------ STYLE OVERRIDES

  /** The CSS className for the element. A function may be provided to compute the class based on component state. */
  className?: AriaRadioProps['className'];
  /** The inline style for the element. A function may be provided to compute the style based on component state. */
  style?: AriaRadioProps['style'];

  // ------ EVENTS

  /** Handler that is called when focus is lost. */
  onBlur?: AriaRadioProps['onBlur'];
  /** Handler that is called when focus is received. */
  onFocus?: AriaRadioProps['onFocus'];
  /** Handler that is called when the focus status changes. */
  onFocusChange?: AriaRadioProps['onFocusChange'];
  /** Handler that is called when the hover state changes. */
  onHoverChange?: AriaRadioProps['onHoverChange'];
  /** Handler that is called when a hover interaction ends. */
  onHoverEnd?: AriaRadioProps['onHoverEnd'];
  /** Handler that is called when a hover interaction starts. */
  onHoverStart?: AriaRadioProps['onHoverStart'];
  /** Handler that is called when a key is pressed. */
  onKeyDown?: AriaRadioProps['onKeyDown'];
  /** Handler that is called when a key is released. */
  onKeyUp?: AriaRadioProps['onKeyUp'];

  // ------ ACCESSIBILITY

  /** The element's unique identifier. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id | MDN}. */
  id?: string;
  /** Defines a string value that labels the current element. */
  'aria-label'?: string;
  /** Identifies the element (or elements) that labels the current element. */
  'aria-labelledby'?: AriaRadioProps['aria-labelledby'];
  /** Identifies the element (or elements) that describes the object. */
  'aria-describedby'?: AriaRadioProps['aria-describedby'];
  /** Identifies the element (or elements) that provide a detailed, extended description for the object. */
  'aria-details'?: AriaRadioProps['aria-details'];
};

/**
 * `Radio` represents an individual option from a list of mutually exclusive options.
 *  Each `Radio` must be wrapped in a {@link RadioGroup} component.
 *
 * ```tsx
 * <RadioGroup label="Radio Group Label" defaultValue="option1">
 *   <Radio value="option1">Option 1</Radio>
 *   <Radio value="option2">Option 2</Radio>
 *   <Radio value="option3" isDisabled>Option 3</Radio>
 *   <Radio value="option4">Option 4</Radio>
 * </RadioGroup>
 * ```
 */
export const Radio = ({ className, ...props }: RadioProps) => {
  return (
    <AriaRadio
      className={(renderProps) =>
        mergeClassNames([styles.radio, className], renderProps)
      }
      data-zds
      // Passes children as a prop to handle both ReactNode and function children
      {...props}
    />
  );
};
